import React, { useEffect, useState } from "react";
import { sortBy, isEmpty } from "lodash";
import {
  H5,
  H6,
  Divider,
  Box,
  RadioButton,
  Checkbox,
  Badge,
} from "orcs-design-system";
import { useHistory } from "react-router-dom";
import { getSearchPath } from "src/consts/urlPaths";
import MatchPropType from "src/custom-prop-types/match";

import { useGlobalSearchContext } from "src/components/Search/GlobalSearchContext";
import { getGroupTypeBadgeVariant } from "src/util/group";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import usePrevious from "src/util/usePrevious";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import {
  filterTypeLabels,
  filterTypes,
} from "../../../pages/SearchPage/SearchPage.config";
import FilterSearchSection from "./FilterSearchSection";

const FilterSearchResults = ({ match }) => {
  const {
    searchQuery,
    defaultFilters,
    setDefaultFilters,
    defaultTeamTypes,
    setDefaultTeamTypes,
    addDefaultFilter,
    removeDefaultFilter,
    addDefaultTeamType,
    removeDefaultTeamType,
  } = useGlobalSearchContext();

  const { params } = match;
  const { type } = params;
  const history = useHistory();
  const previouseSearchQuery = usePrevious(searchQuery);
  const [isInitialized, setIsInitialized] = useState(false);

  const onHandleNavigate = (searchType, query) => (e) => {
    setDefaultFilters([]);
    setDefaultTeamTypes([]);
    history.push(getSearchPath(searchType, query));
    e.stopPropagation();
    e.preventDefault();
    trackEvent(EVENT_TRACKING.SEARCH_FILTERED, {
      setting: "filter",
      value: searchType,
    });
  };

  const onHandleSubfilter = (e) => {
    if (e.target.checked) {
      addDefaultFilter(e.target.name);
      trackEvent(EVENT_TRACKING.SEARCH_FILTERED, {
        setting: "sub_filter",
        value: e.target.name,
      });
    } else {
      removeDefaultFilter(e.target.name);
    }
  };

  const onHandleTeamType = (e) => {
    if (e.target.checked) {
      addDefaultTeamType(e.target.name);
      trackEvent(EVENT_TRACKING.SEARCH_FILTERED, {
        setting: "team_type",
        value: true,
      });
    } else {
      removeDefaultTeamType(e.target.name);
    }
  };

  useEffect(() => {
    // Skip URL updates until we've initialized from the URL
    if (!isInitialized) {
      return;
    }

    const url = getSearchPath(
      type,
      searchQuery,
      !(!!previouseSearchQuery && !searchQuery)
    );
    const searchParams = [];
    if (!isEmpty(defaultFilters)) {
      searchParams.push(`filters=${defaultFilters.join()}`);
    }

    if (!isEmpty(defaultTeamTypes)) {
      searchParams.push(`team_types=${defaultTeamTypes.join()}`);
    }

    const currentUrl = !isEmpty(searchParams)
      ? `${url}?${searchParams.join("&")}`
      : url;
    const prevPath = history?.location?.search
      ? `${history?.location?.pathname}${history?.location?.search}`
      : history?.location?.pathname;

    if (decodeURI(currentUrl) !== decodeURI(prevPath)) {
      history.push(currentUrl);
    }
  }, [
    defaultFilters,
    defaultTeamTypes,
    type,
    searchQuery,
    history,
    previouseSearchQuery,
    isInitialized,
  ]);

  // Parse URL query parameters on mount and when type changes
  useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const filtersParam = searchParams.get("filters");
    const teamTypesParam = searchParams.get("team_types");

    if (filtersParam) {
      const filters = filtersParam.split(",").filter(Boolean);
      setDefaultFilters(filters);
    } else {
      setDefaultFilters([]);
    }

    if (teamTypesParam) {
      const teamTypes = teamTypesParam.split(",").filter(Boolean);
      setDefaultTeamTypes(teamTypes);
    } else {
      setDefaultTeamTypes([]);
    }

    // Mark as initialized after setting the filters
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, setDefaultFilters, setDefaultTeamTypes]);

  const workspace = useWorkspace();

  return (
    <div id="keep-open-on-click-outside">
      <Box mb="r">
        <H5 fontWeight="bold">Search filters</H5>
        <Box my="r">
          <Divider light />
        </Box>
        <H6 fontWeight="bold" mb="r">
          What are you looking for?
        </H6>
        <Box mr="r" mb="r">
          <RadioButton
            name="search-filter-btn"
            label={filterTypeLabels.ALL}
            checked={type === filterTypes.ALL}
            onClick={(e) => {
              onHandleNavigate(filterTypes.ALL, searchQuery)(e);
            }}
            mt="r"
            mb="r"
          />
          <RadioButton
            name="search-filter-btn"
            label={filterTypeLabels.PEOPLE}
            checked={type === filterTypes.PEOPLE}
            onClick={(e) => {
              onHandleNavigate(filterTypes.PEOPLE, searchQuery)(e);
            }}
            mt="r"
            mb="r"
          />
          <RadioButton
            name="search-filter-btn"
            label={filterTypeLabels.TEAM}
            checked={type === filterTypes.TEAMS}
            onClick={(e) => {
              onHandleNavigate(filterTypes.TEAMS, searchQuery)(e);
            }}
            mt="r"
            mb="r"
          />
          <RadioButton
            name="search-filter-btn"
            label={filterTypeLabels.TAGS}
            checked={type === filterTypes.TAGS}
            onClick={(e) => {
              onHandleNavigate(filterTypes.TAGS, searchQuery)(e);
            }}
            mt="r"
            mb="r"
          />
        </Box>

        {type === filterTypes.PEOPLE && (
          <FilterSearchSection
            items={[
              { label: "First name", name: "firstName" },
              { label: "Last name", name: "surname" },
              { label: "Job Title", name: "jobTitle" },
            ]}
            onChange={onHandleSubfilter}
            checkedItems={defaultFilters}
          />
        )}
        {type === filterTypes.TEAMS && (
          <Box mr="r" mt="r">
            <H6 fontWeight="bold" mb="r">
              Filter results by team type:
            </H6>
            {sortBy(Object.values(workspace?.config?.groupTypes), "name").map(
              (groupType) => {
                return (
                  <Checkbox
                    key={groupType.id}
                    label={
                      <Badge variant={getGroupTypeBadgeVariant(groupType)}>
                        {groupType.name}
                      </Badge>
                    }
                    name={groupType.id}
                    checked={defaultTeamTypes.includes(groupType.id)}
                    onChange={onHandleTeamType}
                  />
                );
              }
            )}
          </Box>
        )}
      </Box>
    </div>
  );
};

FilterSearchResults.propTypes = {
  match: MatchPropType.isRequired,
};

export default FilterSearchResults;
